import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Mail, Lock, Sparkles, Key, ArrowRight } from "lucide-react";

const C = {
  bg: "var(--bc-bg, #050507)",
  surface: "var(--bc-panel, #09090B)",
  border: "var(--bc-border, rgba(255,255,255,0.07))",
  text: "var(--bc-ink, #FFFFFF)",
  muted: "var(--bc-sub, #71717A)",
  accent: "var(--bc-accent, #C8FF00)",
  cyan: "#06B6D4",
  red: "#EF4444",
  violet: "#A78BFA",
};

export default function AuthPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { register, login, isAuthenticated } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const redirect = params.get("redirect") || "/";
  const justReset = params.get("just_reset") === "1";

  useEffect(() => {
    if (isAuthenticated) navigate(redirect, { replace: true });
  }, [isAuthenticated, navigate, redirect]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === "register") {
        await register(email, password);
      } else {
        await login(email, password);
      }
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem" }} className="grid-bg">
      <div style={{ width: "100%", maxWidth: 420 }}>
        {/* Brand */}
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div style={{
            fontFamily: "'Barlow Condensed',sans-serif",
            fontWeight: 900, fontSize: "2.5rem",
            letterSpacing: "0.18em", color: C.text,
            textTransform: "uppercase",
            marginBottom: "0.3rem",
          }}>
            Bet<span style={{ color: C.accent }}>Cracker</span>
          </div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", letterSpacing: "0.18em", color: C.muted, textTransform: "uppercase" }}>
            Predictive Football Intelligence
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          padding: "1.75rem 1.5rem",
        }}>
          {/* Tabs */}
          <div style={{ display: "flex", gap: 4, padding: 4, background: "rgba(255,255,255,0.03)", marginBottom: "1.5rem" }}>
            <button
              data-testid="auth-tab-login"
              onClick={() => setMode("login")}
              style={tabStyle(mode === "login", C.accent)}
            >
              Accedi
            </button>
            <button
              data-testid="auth-tab-register"
              onClick={() => setMode("register")}
              style={tabStyle(mode === "register", C.violet)}
            >
              Registrati
            </button>
          </div>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
            {justReset && (
              <div data-testid="auth-reset-banner" style={{
                padding: "0.55rem 0.75rem",
                background: "rgba(34,255,163,0.08)",
                border: "1px solid #22FFA355",
                color: "#22FFA3",
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.72rem",
              }}>
                ✓ Password aggiornata. Accedi con la nuova password.
              </div>
            )}
            <Field icon={<Mail size={14} />} label="Email">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@example.com"
                data-testid="auth-email-input"
                autoComplete="email"
                style={inputStyle(C)}
              />
            </Field>
            <Field icon={<Lock size={14} />} label="Password">
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "register" ? "Min 8 caratteri, con numeri" : "••••••••"}
                data-testid="auth-password-input"
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                style={inputStyle(C)}
              />
            </Field>

            {error && (
              <div data-testid="auth-error" style={{
                padding: "0.55rem 0.75rem",
                background: `${C.red}15`,
                border: `1px solid ${C.red}55`,
                color: C.red,
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.72rem",
              }}>
                ⚠ {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit"
              style={{
                marginTop: "0.4rem",
                padding: "0.75rem",
                background: busy ? "transparent" : (mode === "register" ? C.violet : C.accent),
                color: busy ? C.muted : "#020202",
                border: `1px solid ${busy ? C.border : "transparent"}`,
                fontFamily: "'Barlow Condensed',sans-serif",
                fontWeight: 800,
                fontSize: "0.9rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                cursor: busy ? "wait" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : (mode === "register" ? <Sparkles size={13} /> : <ArrowRight size={13} />)}
              {busy ? "Attendere…" : (mode === "register" ? "Crea Account" : "Accedi")}
            </button>
          </form>

          {mode === "login" && (
            <div style={{ marginTop: "0.9rem", textAlign: "center" }}>
              <button
                type="button"
                data-testid="auth-forgot-link"
                onClick={() => navigate("/forgot-password")}
                style={{
                  background: "transparent", border: "none", color: C.muted,
                  cursor: "pointer",
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.68rem", letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  textDecoration: "underline",
                }}
              >
                Password dimenticata?
              </button>
            </div>
          )}

          {/* Legacy access code claim hint */}
          <div style={{
            marginTop: "1.5rem",
            paddingTop: "1rem",
            borderTop: `1px solid ${C.border}`,
            fontFamily: "'DM Sans',sans-serif",
            fontSize: "0.74rem",
            color: C.muted,
            lineHeight: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
          }}>
            <Key size={13} style={{ color: C.accent, marginTop: 2, flexShrink: 0 }} />
            <span>
              Hai già un codice <code style={{ color: C.accent }}>BCRK-XXXX</code>?
              {" "}Crea il tuo account e dopo il login potrai collegarlo per ottenere
              <strong style={{ color: C.accent }}> Premium gratis a vita</strong>.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon, label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        fontFamily: "'JetBrains Mono',monospace",
        fontSize: "0.6rem", letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "var(--bc-sub, #71717A)",
        marginBottom: "0.35rem",
      }}>
        {icon}<span>{label}</span>
      </div>
      {children}
    </label>
  );
}

function tabStyle(active, color) {
  return {
    flex: 1,
    padding: "0.55rem 0.5rem",
    background: active ? `${color}1F` : "transparent",
    color: active ? color : "var(--bc-sub, #71717A)",
    border: "none",
    fontFamily: "'Barlow Condensed',sans-serif",
    fontWeight: 700,
    fontSize: "0.78rem",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    cursor: "pointer",
  };
}

function inputStyle(C) {
  return {
    width: "100%",
    padding: "0.7rem 0.85rem",
    background: "rgba(255,255,255,0.03)",
    border: `1px solid ${C.border}`,
    color: C.text,
    fontFamily: "'JetBrains Mono',monospace",
    fontSize: "0.82rem",
    letterSpacing: "0.02em",
    outline: "none",
    transition: "border-color 0.15s",
  };
}
